import React, { useEffect, useState, useCallback, memo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CONFIG from '../../config';
import { getToken } from '../../utils/auth';
import { maskUsername, maskEmail } from '../../utils/anonymizer';

const STATUS_CONFIG = {
    pending: { label: 'Pending', color: 'amber', icon: 'fa-clock' },
    processing: { label: 'Processing', color: 'blue', icon: 'fa-cog' },
    shipped: { label: 'Shipped', color: 'indigo', icon: 'fa-truck' },
    delivered: { label: 'Delivered', color: 'green', icon: 'fa-check-circle' },
    cancelled: { label: 'Cancelled', color: 'red', icon: 'fa-times-circle' },
};
const STATUS_COLORS = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
};

const StatCard = ({ label, value, icon, color }) => (
    <div className={`rounded-2xl border p-5 ${color}`}>
        <div className="flex items-center gap-3 mb-1">
            <i className={`fas ${icon} text-sm`} />
            <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
        </div>
        <p className="text-2xl font-black">{value ?? 0}</p>
    </div>
);

const OrdersAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language?.startsWith('ar');
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [per] = useState(15);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [expandedData, setExpandedData] = useState({});
    const [updatingId, setUpdatingId] = useState(null);

    const navigate = useNavigate();

    const headers = { Authorization: `Bearer ${getToken()}` };

    const fetchStats = useCallback(async () => {
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/orders/admin/stats`, { headers });
            setStats(res.data);
        } catch (e) { console.error(e); }
    }, []); // eslint-disable-line

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/orders/admin/all`, {
                headers,
                params: { page, per, status: statusFilter, search }
            });
            setOrders(res.data.data || []);
            setTotal(res.data.total || 0);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [page, per, statusFilter, search]); // eslint-disable-line

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    const loadOrderDetail = async (id) => {
        if (expandedData[id]) { setExpanded(expanded === id ? null : id); return; }
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/orders/admin/${id}`, { headers });
            setExpandedData(prev => ({ ...prev, [id]: res.data }));
            setExpanded(id);
        } catch (e) { console.error(e); }
    };

    const updateStatus = async (id, status) => {
        setUpdatingId(id);
        try {
            await axios.patch(`${CONFIG.API_URL}/api/orders/admin/${id}/status`, { status }, { headers });
            setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
            if (expandedData[id]) setExpandedData(prev => ({ ...prev, [id]: { ...prev[id], status } }));
            fetchStats();
        } catch (e) { alert('Status update failed'); }
        finally { setUpdatingId(null); }
    };

    const revenue = stats?.revenue || [];

    return (
        <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">Orders</h1>
                    <p className="text-gray-400 text-sm font-medium mt-1">Manage and track customer orders</p>
                </div>
                <button
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
                >
                    <span className={`transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                    {t('admin.common.back', 'Back')}
                </button>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
                    <StatCard label="Total" value={stats.total} icon="fa-shopping-bag" color="bg-gray-50 text-gray-700 border-gray-100" />
                    <StatCard label="Today" value={stats.today} icon="fa-calendar-day" color="bg-brand-primary/5 text-brand-primary border-brand-primary/10" />
                    <StatCard label="Pending" value={stats.byStatus?.pending} icon="fa-clock" color={STATUS_COLORS.amber} />
                    <StatCard label="Processing" value={stats.byStatus?.processing} icon="fa-cog" color={STATUS_COLORS.blue} />
                    <StatCard label="Delivered" value={stats.byStatus?.delivered} icon="fa-check-circle" color={STATUS_COLORS.green} />
                    <StatCard label="Cancelled" value={stats.byStatus?.cancelled} icon="fa-times-circle" color={STATUS_COLORS.red} />
                </div>
            )}

            {/* Revenue */}
            {revenue.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-8">
                    {revenue.map(r => (
                        <div key={r.currency} className="bg-green-50 border border-green-100 rounded-2xl px-5 py-3">
                            <p className="text-[10px] font-black uppercase tracking-widest text-green-600">{r.currency} Revenue</p>
                            <p className="text-xl font-black text-green-800">{parseFloat(r.total).toLocaleString()}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3 mb-8 bg-gray-50 p-4 rounded-3xl">
                <div className="relative flex-1">
                    <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
                    <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                        placeholder="Search by name, email, phone, order ID..."
                        className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white border-none font-medium text-sm focus:ring-2 focus:ring-brand-primary/20 transition-all" />
                </div>
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                    className="py-3 px-4 rounded-2xl bg-white border-none font-bold text-sm focus:ring-2 focus:ring-brand-primary/20">
                    <option value="all">All Statuses</option>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <button onClick={fetchOrders} className="px-6 py-3 bg-brand-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg hover:scale-105 transition-all">
                    Search
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="p-16 text-center text-gray-300 animate-pulse font-bold">Loading orders...</div>
            ) : orders.length === 0 ? (
                <div className="p-16 text-center">
                    <i className="fas fa-box-open text-4xl text-gray-100 mb-4 block" />
                    <p className="text-gray-300 font-bold">No orders found</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {orders.map(order => {
                        const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                        const isExp = expanded === order.id;
                        const detail = expandedData[order.id];
                        return (
                            <div key={order.id} className="border border-gray-100 rounded-2xl overflow-hidden hover:border-brand-primary/20 transition-all">
                                {/* Row */}
                                <div className="flex flex-wrap items-center gap-4 p-5 cursor-pointer hover:bg-gray-50/50 transition-all" onClick={() => loadOrderDetail(order.id)}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="font-black text-xs text-gray-900 font-mono">#{order.id.slice(-8)}</span>
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${STATUS_COLORS[sc.color]}`}>
                                                <i className={`fas ${sc.icon} mr-1`} />{sc.label}
                                            </span>
                                        </div>
                                        <p className="text-xs font-bold text-gray-600">{maskUsername(order.username)} · {maskEmail(order.email)}</p>
                                    </div>
                                    <div className="text-center hidden md:block">
                                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Method</p>
                                        <p className="text-xs font-black text-gray-700 capitalize">{order.payment_method?.replace(/_/g, ' ')}</p>
                                    </div>
                                    <div className="text-center hidden md:block">
                                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Amount</p>
                                        <p className="text-sm font-black text-gray-900">{parseFloat(order.total_amount).toFixed(2)} {order.currency}</p>
                                    </div>
                                    <div className="text-center hidden lg:block">
                                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Date</p>
                                        <p className="text-xs font-black text-gray-600">{new Date(order.created_at).toLocaleDateString()}</p>
                                    </div>
                                    {/* Status dropdown */}
                                    <div onClick={e => e.stopPropagation()}>
                                        <select
                                            value={order.status}
                                            onChange={e => updateStatus(order.id, e.target.value)}
                                            disabled={updatingId === order.id}
                                            className={`py-2 px-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-none cursor-pointer focus:ring-2 focus:ring-brand-primary/20 ${STATUS_COLORS[sc.color]}`}>
                                            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                        </select>
                                    </div>
                                    <i className={`fas fa-chevron-${isExp ? 'up' : 'down'} text-gray-300 text-xs`} />
                                </div>

                                {/* Expanded Detail */}
                                {isExp && detail && (
                                    <div className="px-5 pb-5 bg-gray-50/30 border-t border-gray-100">
                                        <div className="grid md:grid-cols-2 gap-4 pt-4 mb-4">
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Shipping Address</p>
                                                <p className="text-sm text-gray-700 font-medium">{detail.shipping_address}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Contact</p>
                                                <p className="text-sm text-gray-700 font-medium">{detail.contact_phone}</p>
                                            </div>
                                            {detail.notes && (
                                                <div className="md:col-span-2">
                                                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Notes</p>
                                                    <p className="text-sm text-gray-600">{detail.notes}</p>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Order Items</p>
                                        <div className="space-y-2">
                                            {(detail.items || []).map(item => (
                                                <div key={item.id} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100">
                                                    {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />}
                                                    <div className="flex-1">
                                                        <p className="text-xs font-black text-gray-900">{item.name}</p>
                                                        <p className="text-[10px] text-gray-400 font-bold">Qty: {item.quantity}</p>
                                                    </div>
                                                    <p className="text-xs font-black text-gray-700">{(item.price_at_time * item.quantity).toFixed(2)}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination */}
            {total > per && (
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
                    <p className="text-xs font-bold text-gray-400">{total} total orders</p>
                    <div className="flex gap-3 items-center">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-40 transition-all font-bold text-sm">←</button>
                        <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Page {page} / {Math.ceil(total / per)}</span>
                        <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / per)}
                            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-40 transition-all font-bold text-sm">→</button>
                    </div>
                </div>
            )}
        </div>
    );
});

export default OrdersAdmin;
